import * as React from "react";
import classnames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { treeLoopFunction } from 'utils/libHelpers/treeLoopFunction';
import { foldersStore } from "../Stores/FoldersStore";
import { observer } from "mobx-react";
import { documentsTableStore } from "../Stores/DocumentsTableStore";
import { TableLoader } from "Components/ScrollableTable/components/TableLoader";
import i18n, { I18N_NS } from "Config/i18n";
import { isEmpty } from "utils/isEmpty";
import { MoveFilesFormProps, MoveFilesForm } from "../Components/MoveFilesForm";
import { modalProps } from "Pages/Project/QA/utils/modalProps";
import { withModal, WithModal } from "Components/General/Modal/withModal";
import { useTranslation } from "react-i18next";
import { DocumentsTree } from "./DocumentsTree";
import { message } from "antd";
import { MoveFolderForm } from "./MoveFolderForm";
import { useEffect, useState } from "react";

interface DocumentsSidebarProps extends WithModal {
  onFolderSelect: (folderId: number) => void;
}

// const loop = treeLoopFunction(Tree);

export const DocumentsSidebar = withModal(
  observer(({ showModal }: DocumentsSidebarProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [height, setHeight] = useState<number>(null);
    // const [width, setWidth] = useState<number>(null);

    const { t } = useTranslation(I18N_NS.PROJECT);
    useEffect(() => {
      setSidebarHeight();
      makeResizableSidebar();
      window.addEventListener("resize", setSidebarHeight);
      return () => {
        window.removeEventListener("resize", setSidebarHeight);
      };
    }, []);

    const makeResizableSidebar = () => {
      const sidebar: HTMLDivElement = document.querySelector(".documents-sidebar");
      const resizer: HTMLDivElement = document.querySelector(".documents-sidebar-wrapper .resizable");

      const resize = (e) => {
        const size = e.pageX - sidebar.getBoundingClientRect().left;
        sidebar.setAttribute("style", `width: ${size}px; min-width: ${size}px`);
      };

      const stopResize = () => {
        const sidebarWidth = sidebar.style.width.split("px")[0];
        if (Number(sidebarWidth) < 250) {
          toggleSidebar();
        }
        window.removeEventListener("mousemove", resize);
        window.removeEventListener("mouseup", stopResize);
      };

      resizer.addEventListener("mousedown", (e: MouseEvent) => {
        e.preventDefault();
        window.addEventListener("mousemove", resize);
        window.addEventListener("mouseup", stopResize);
      });
    };

    const setSidebarHeight = () => {
      const documentHeight: number = document.documentElement.clientHeight;
      const headerHeight: number = document.getElementsByTagName("header")[0].clientHeight;
      setHeight(documentHeight - headerHeight);
    };

    const setSidebarWidth = (isOpen: boolean) => {
      const sidebar: HTMLDivElement = document.querySelector(".documents-sidebar");
      let size: number;
      isOpen ? (size = 280) : (size = 15);
      sidebar.setAttribute("style", `width: ${size}px; min-width: ${size}px`);
    };

    const toggleSidebar = () => {
      setIsOpen((prev) => !prev);
      // this.setState({ isOpen: !this.state.isOpen }, () => {
      //   setSidebarWidth(this.state.isOpen);
      // });
    };

    useEffect(() => {
      setSidebarWidth(isOpen);
    }, [isOpen]);

    const handleFolderSelect = (folderId) => {
      if (folderId) {
        if (folderId === documentsTableStore.currentFolderId) {
          return;
        }
        documentsTableStore.goToFolder(folderId);
      } else {
        console.warn("Folder id not found in Documents sidebar", folderId);
      }
    };

    const handleLoad = async (id: number) => {
      return await foldersStore.fetchFolder(id);
    };

    const handleExpand = (expandedKeys: number, expanded: boolean) => {
      foldersStore.setExpandedKey(expandedKeys, expanded);
    };

    const selectedKeys = () => {
      const key: number = documentsTableStore.currentFolderId;
      if (key) {
        return [key];
      }
      return [];
    };

    const renderRefreshIcon = () => {
      return (
        <span
          className="refresh"
          onClick={async () => {
            foldersStore.clear();
            await foldersStore.reFetch();
          }}
        >
          <FontAwesomeIcon icon={"sync"} fixedWidth={true} className="mr-5" />
          <span>{i18n.t("Refresh", { ns: I18N_NS.PROJECT }) as string}</span>
        </span>
      );
    };

    const refreshTreeOnSubmitFolder = async () => {
      await documentsTableStore.refresh();
      foldersStore.clear();
      await foldersStore.fetch(undefined);
    };

    const onDropModalHandler = (event: React.DragEvent, nodeId: number) => {
      const dataTransfer = event.dataTransfer.getData("text");
      const dragRowData = JSON.parse(dataTransfer);
      if (dragRowData.type === "FOLDER" && dragRowData.id === nodeId) {
        return;
      }
      if (dragRowData.type === "FOLDER" && dragRowData.folderMove) {
        showModal(MoveFolderForm, {
          modalProps: { footer: null, title: t("Move directory") },
          componentProps: {
            folderId: dragRowData.id,
            stayInCurrentFolder: true,
            initialFolderId: nodeId,
            onSubmitSuccess: refreshTreeOnSubmitFolder,
          },
        });
      } else if (dragRowData.type !== "FOLDER" && dragRowData.fileMove) {
        showModal<Partial<MoveFilesFormProps>>(MoveFilesForm, {
          modalProps: { ...modalProps, title: t("Move files") },
          componentProps: {
            files: [dragRowData],
            defaultFolderId: nodeId,
            onSubmitSuccess: documentsTableStore.goToFolder,
            onMoveFile: documentsTableStore.moveFiles,
          },
        });
      } else {
        message.error(t("You do not have access to move files."));
      }
    };

    const sidebarClassName = classnames("documents-sidebar", {
      "is-open": isOpen,
    });
    const toggleIconClassName = classnames("toggle-icon", {
      "is-open": isOpen,
    });
    const toggleResizerClassName = classnames("resizable", {
      block: isOpen,
    });
    const switcherIcon = (expanded: boolean) => {
      return (
        <FontAwesomeIcon
          icon={"caret-right"}
          fixedWidth={true}
          className="mr-5"
          style={
            expanded
              ? { transform: "rotate(90deg)", cursor: "pointer" }
              : { transform: "rotate(0deg)", cursor: "pointer" }
          }
        />
      );
    };

    return (
      <div className="documents-sidebar-wrapper" style={{ height: height }}>
        <div className={sidebarClassName}>
          <TableLoader
            loading={foldersStore.state === "LOADING" && !foldersStore.data.children}
            style={{ backgroundColor: "#e0e5ed" }}
          />
          {!isEmpty(foldersStore.data) && (
            <>
              {renderRefreshIcon()}
              <DocumentsTree
                defaultExpandedKeys={foldersStore.expandedKeys}
                defaultSelectedKeys={[foldersStore.rootFolderId]}
                onSelect={handleFolderSelect}
                onExpand={handleExpand}
                selectedKeys={selectedKeys()}
                loadData={handleLoad}
                style={{ fontSize: 13 }}
                treeData={foldersStore.treeData}
                droppable={true}
                switcherIcon={switcherIcon}
                onDrop={onDropModalHandler}
                isDragging={documentsTableStore.isDragging}
              />
            </>
          )}
        </div>
        <div className={toggleResizerClassName}>
          <div className="line" />
        </div>
        <div className={toggleIconClassName} onClick={toggleSidebar}>
          <div className="toggle-background" />
          <FontAwesomeIcon icon={["far", "chevron-right"]} size={"lg"} className="documents-sidebar-icon" />
        </div>
      </div>
    );
  })
);
